import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faLinkedin,
  faFacebook,
  faInstagram,
  faXTwitter,
  faYoutube,
  faTiktok,
  faPinterest,
  faThreads,
  faGoogle,
  faBluesky,
} from '@fortawesome/free-brands-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { PeriodPills } from './PeriodPills';
import { HorizontalCardCarousel } from './HorizontalCardCarousel';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { HoverCard } from '@/components/ui/HoverCard';
import { RetryImage } from '@/components/ui/RetryImage';
import { PlaceholderImage } from '@/components/ui/PlaceholderImage';
import { DashboardPostPopover } from './DashboardPostPopover';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { usePublishedQueue, PublishedQueueItem } from '@/hooks/useDashboardStats';
import { useTimezone } from '@/contexts/TimezoneContext';

/**
 * Published Section - Successfully published posts (Carousel layout)
 *
 * Features:
 * - Horizontal carousel with arrow navigation
 * - Period filtering (today/week/month) - defaults to 'month'
 * - Green theme for published success state
 * - FontAwesome platform icons showing where posts were published
 * - Caption-based display
 * - HoverCard popover matching CalendarPostPopover style
 * - Timezone-aware published dates
 */

type Period = 'today' | 'week' | 'month';

// FontAwesome platform icons (same as PublishingSection)
const PLATFORM_FA_ICONS: Record<string, IconDefinition> = {
  LINKEDIN: faLinkedin,
  FACEBOOK: faFacebook,
  INSTAGRAM: faInstagram,
  TWITTER: faXTwitter,
  YOUTUBE: faYoutube,
  TIKTOK: faTiktok,
  PINTEREST: faPinterest,
  THREADS: faThreads,
  GOOGLE_MY_BUSINESS: faGoogle,
  BLUESKY: faBluesky,
};

// Platform colors for icons
const PLATFORM_COLORS: Record<string, string> = {
  LINKEDIN: '#0A66C2',
  FACEBOOK: '#1877F2',
  INSTAGRAM: '#E4405F',
  TWITTER: '#000000',
  YOUTUBE: '#FF0000',
  TIKTOK: '#000000',
  PINTEREST: '#E60023',
  THREADS: '#000000',
  GOOGLE_MY_BUSINESS: '#4285F4',
  BLUESKY: '#0085FF',
};

/**
 * Skeleton Card Component
 */
const SkeletonCard = () => (
  <div className="flex-none w-[160px] aspect-square bg-gray-200 rounded-xl animate-pulse" />
);

/**
 * Post Card Component
 */
interface PostCardProps {
  item: PublishedQueueItem;
}

const PostCard = ({ item }: PostCardProps) => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { formatRelativeDate } = useTimezone();
  const navigate = useNavigate();

  const handleClick = () => {
    navigate('/calendar');
  };

  // HoverCard popover content - GREEN gradient header for published
  const popoverContent = (
    <DashboardPostPopover
      gradient="from-emerald-500 to-emerald-600"
      headerIcon={ICONS.CHECK}
      headerLabel={t('dashboard:dashboard_section_published')}
      headerLines={[
        { icon: ICONS.CALENDAR, text: item.calendarName },
        { icon: ICONS.CLOCK, text: formatRelativeDate(item.publishedAt) },
      ]}
      postMedia={item.postMedia || []}
      caption={item.caption || ''}
    >
      {/* Channels section */}
      {item.channels.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t('common:published_channels', { defaultValue: 'Published to' })} ({item.channels.length})
          </p>
          <div className="flex flex-wrap gap-2.5">
            {item.channels.map((ch, i) => (
              <Tooltip
                key={i}
                content={
                  <div className="text-xs">
                    <p className="font-medium">{ch.platformUsername || ch.platform}</p>
                    <p className="text-gray-400">{ch.platform}</p>
                    {ch.externalUrl && <p className="text-emerald-400 mt-0.5">{t('published_click_to_view')}</p>}
                  </div>
                }
                className="!z-[10001]"
              >
                <div className="relative">
                  <div
                    className={cn(
                      'w-8 h-8 rounded-full overflow-hidden flex items-center justify-center',
                      'ring-2 ring-offset-1 ring-emerald-500',
                      ch.externalUrl && 'cursor-pointer hover:opacity-80 transition-opacity'
                    )}
                    onClick={(e) => {
                      if (ch.externalUrl) {
                        e.stopPropagation();
                        window.open(ch.externalUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    {ch.profilePhotoUrl ? (
                      <img src={ch.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                          className="text-gray-500 w-4 h-4"
                          aria-hidden="true"
                        />
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white shadow-sm flex items-center justify-center">
                    <FontAwesomeIcon
                      icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                      className="text-gray-600 text-[10px]"
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
        </div>
      )}
    </DashboardPostPopover>
  );

  // Card trigger - Square layout with full-bleed thumbnail
  const cardTrigger = (
    <div
      onClick={handleClick}
      className="flex-none w-[160px] aspect-square rounded-xl overflow-hidden relative bg-gray-100 cursor-pointer hover:shadow-lg hover:scale-[1.03] transition-all duration-200"
    >
      {/* Full-bleed thumbnail */}
      {item.thumbnailUrl ? (
        <RetryImage
          src={item.thumbnailUrl}
          alt=""
          className="w-full h-full object-cover"
          fallback={<PlaceholderImage />}
        />
      ) : (
        <PlaceholderImage />
      )}

      {/* Top overlay: Channel avatars + green check dot */}
      <div className="absolute top-0 inset-x-0 p-2 flex items-center gap-1">
        {item.channels.slice(0, 3).map((ch, index) => (
          <Tooltip key={`${ch.platform}-${index}`} content={ch.platformUsername || ch.platform}>
          <div
            className={cn(
              "w-6 h-6 rounded-full overflow-hidden bg-white/90 backdrop-blur-sm flex items-center justify-center flex-shrink-0 ring-1 ring-white/50",
              ch.externalUrl && "cursor-pointer hover:ring-emerald-300"
            )}
            onClick={(e) => {
              if (ch.externalUrl) {
                e.stopPropagation();
                window.open(ch.externalUrl, '_blank', 'noopener,noreferrer');
              }
            }}
          >
            {ch.profilePhotoUrl ? (
              <img src={ch.profilePhotoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              <FontAwesomeIcon
                icon={PLATFORM_FA_ICONS[ch.platform] || faGlobe}
                className="w-3 h-3"
                style={{ color: PLATFORM_COLORS[ch.platform] || '#6B7280' }}
                aria-hidden="true"
              />
            )}
          </div>
          </Tooltip>
        ))}
        {item.channels.length > 3 && (
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-white/90 backdrop-blur-sm text-[10px] font-semibold text-gray-600 ring-1 ring-white/50">
            +{item.channels.length - 3}
          </span>
        )}
        {/* Green published dot */}
        <div className="ml-auto">
          <div className="w-2.5 h-2.5 rounded-full ring-2 ring-white/50 bg-emerald-400" />
        </div>
      </div>

      {/* Bottom gradient overlay */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-2 pt-8">
        {/* Calendar name badge */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CALENDAR} size="xs" className="text-white" ariaHidden />
          <span className="text-[11px] text-white font-medium drop-shadow-sm">
            {item.calendarName}
          </span>
        </div>
        {/* Published date */}
        <div className="flex items-center gap-1 mb-0.5">
          <Icon name={ICONS.CHECK} size="xs" className="text-emerald-300" ariaHidden />
          <span className="text-[10px] text-white/80 drop-shadow-sm">
            {formatRelativeDate(item.publishedAt)}
          </span>
        </div>
        {/* Caption */}
        <p className={cn(
          'text-[11px] text-white/90 line-clamp-1 drop-shadow-sm',
          !item.caption && 'italic'
        )}>
          {item.caption ? (item.caption.length > 40 ? `${item.caption.slice(0, 40)}...` : item.caption) : t('common:published_no_caption', { defaultValue: 'No caption' })}
        </p>
      </div>
    </div>
  );

  return (
    <HoverCard
      trigger={cardTrigger}
      side="right"
      align="start"
    >
      {popoverContent}
    </HoverCard>
  );
};

/**
 * Empty State
 */
const EmptyState = () => {
  const { t } = useTranslation('dashboard');

  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full grid place-items-center mb-4">
        <Icon name={ICONS.CHECK_CIRCLE} size="xl" color="text-gray-400" ariaHidden />
      </div>
      <p className="text-gray-500 text-sm">
        {t('dashboard_published_empty', { defaultValue: 'No published posts for this period' })}
      </p>
    </div>
  );
};

/**
 * Main Published Section Component
 */
export const PublishedSection = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>('month');
  const { data, isLoading } = usePublishedQueue(period);

  const handleViewAll = () => {
    navigate('/creator?publishingStatus=published');
  };

  return (
    <div>
      <div className="mb-3 flex justify-end">
        <PeriodPills value={period} onChange={setPeriod} count={data?.items.length} />
      </div>

      {isLoading && (
        <div className="flex gap-3 overflow-x-hidden">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
      )}

      {!isLoading && data?.items.length === 0 && <EmptyState />}

      {!isLoading && data && data.items.length > 0 && (
        <HorizontalCardCarousel>
          {data.items.map((item) => (
            <PostCard key={item.id} item={item} />
          ))}
        </HorizontalCardCarousel>
      )}

      <div className="mt-3 text-center">
        <button
          onClick={handleViewAll}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
        >
          {t('dashboard_published_view_all', { defaultValue: 'View all published posts →' })}
        </button>
      </div>
    </div>
  );
};
